import { Inject, Injectable } from '@angular/core';
import { MCustomer } from '../models/customer.model';
import { MStores } from '../models';
import { LanguageService } from '../login/language.service';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root'
})
export class LocaleMapperService {

  constructor(private readonly languageService: LanguageService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cookieService: CookieService) {
  }

  getStoredPreferredStoreCode(): string {
    return sessionStorage.getItem('preferredStore') || 'all';
  }

  getPreferredStoreForCustomer(customer: MCustomer): MStores {
    let result: MStores;

    // Middleware flow: If customer doesn't have stores array, create a default one with language from localStorage
    if (!customer.stores || customer.stores.length === 0) {
      const language = localStorage.getItem('language') || 'en_US';
      const defaultStore: MStores = {
        locale: language,
        store_code: customer.default_store_code || 'default',
        is_default: true,
        preferred: true
      };
      customer.stores = [defaultStore];
      return defaultStore;
    }

    const landingLangCode = localStorage.getItem('landingLang');
    // Depending only on locale language
    const storedPreferredLocale =
      sessionStorage.getItem('preferredLocale') !== null
        ? sessionStorage.getItem('preferredLocale')
        : landingLangCode;

    //Checking if user has access to selected language
    const isLocaleAvailableForUser = !!customer.stores.find(
      store => store.locale === storedPreferredLocale
    );
    if (storedPreferredLocale && isLocaleAvailableForUser) {
      // preferred locale was determined before, let's update it in the backend response
      // update the store with selected language
      customer.stores.forEach((store: MStores) => {
        if (store.locale === storedPreferredLocale) {
          result = store;
        }
      });
      localStorage.removeItem('redirect');
    } else {
      // preferred locale must be determined from the backend response
      customer.stores.forEach((store: MStores) => {
        if (customer.default_store_code === store.store_code) {
          this.updateLandingAndCookie(store.locale, store.store_code);
          // update session storage with correct values
          this.updateSessionAndLanguageService(store.locale, store.store_code);
          this.reloadApplication();
        }
      });
    }
    return result;
  }

  setPreferredLanguage(customer: MCustomer, preferredStore: MStores): MCustomer {
     if (!customer.stores || customer.stores.length === 0) {
      customer.stores = [preferredStore];
    } else {
      customer.stores.forEach((iStore: MStores) => {
        if (preferredStore.store_code === iStore.store_code) {
          iStore['preferred'] = true;
        } else {
          iStore['preferred'] = false;
        }
      });
    }
    
    customer.default_store_code = preferredStore.store_code;
    sessionStorage.setItem('preferredLocale', preferredStore.locale);
    sessionStorage.setItem('preferredStore', preferredStore.store_code);

    return customer;
  }

  updateLandingAndCookie(locale: string, storeCode: string) {
    // update session storage landing values
    sessionStorage.setItem('landingStoreCode', storeCode);
    sessionStorage.setItem('landingLang', locale);

    // update local storage landing values
    localStorage.setItem('landingStoreCode', storeCode);
    localStorage.setItem('landingLang', locale);

    // update cookie value
    const cookieLanguage = this.languageService.supportedLocales[locale].languageCode;
    const expirationDate = new Date();
    // Expiration time of the cookie is set to 30 days.
    expirationDate.setTime(expirationDate.getTime() + 30 * 24 * 60 * 60 * 1000);

    this.cookieService.set('loginPreferedLocale', cookieLanguage, {path: '/',expires: expirationDate,domain:this.document.location.hostname});
  }

  updateSessionAndLanguageService(locale: string, storeCode: string) { 
    sessionStorage.setItem('preferredLocale', locale);
    sessionStorage.setItem('preferredStore', storeCode);
    // update @(LOCALE_ID) value
    this.languageService.setLanguage(locale);
  }

  private reloadApplication() {
    const redirect = localStorage.getItem('redirect');
    const source = localStorage.getItem('source');

    const isValidSource = source?.toLowerCase() === 'ash' || source?.toLowerCase() === 'snow';
    const isSafeRedirect= typeof redirect === 'string' && redirect.startsWith('/') && !redirect.toLowerCase().startsWith('//');
    // reload the application
    const normalizedPath = this.normalizePath(redirect);

    if(isValidSource && isSafeRedirect) {
        localStorage.removeItem('redirect');
        this.performSafeRedirect(normalizedPath);
     } else {
        this.document.location.reload();
     }
  }

  private normalizePath(path: string): string {
    try {
      let normalized = decodeURIComponent(path);
      
      // Remove duplicate slashes but keep leading slash
      normalized = normalized.replace(/\/+/g, '/');
      
      const segments = normalized.split('/').filter(segment => segment !== '');
      const resolvedSegments: string[] = [];
      
      for (const segment of segments) {
        if (segment === '..') {
          resolvedSegments.pop();
        } else if (segment !== '.') {
          resolvedSegments.push(segment);
        }
      }
      return '/' + resolvedSegments.join('/');
    } catch (error) {
      console.error('Path normalization error:', error);
      return '/'; // Return root path as fallback
    }
 } 

  private performSafeRedirect(redirectPath: string): void {
    try {
      const fullUrl = this.document.location.origin + redirectPath;
      const url = new URL(fullUrl);
      
      if (url.origin !== this.document.location.origin) {
        console.warn('Redirect destination origin mismatch, reloading instead');
        this.document.location.reload();
        return;
      }
      if (this.normalizePath(url.pathname) !== redirectPath) {
        console.warn('Normalized path mismatch, reloading instead');
        this.document.location.reload();
        return;
      }
      this.document.location.href = fullUrl;
    } catch (error) {
      console.error('Invalid redirect URL constructed:', error);
      this.document.location.reload();
    } 
  }
}